import React from 'react'
import { Tooltip } from '@mui/material'
import {
  DarkMode as DarkModeIcon, LightMode as LightModeIcon, BlockRounded, ShieldOutlined, SupportAgentOutlined, SmartToyOutlined,
  PictureAsPdfOutlined, HubOutlined, TimerOutlined, SpeedOutlined, Telegram as TelegramIcon, PsychologyOutlined, MailOutlineRounded, VerifiedUserOutlined, InsightsOutlined, InterestsOutlined, TabOutlined,
} from '@mui/icons-material'
import { PrivatePage } from '../component/PagePrivate'
import { ViewToggle } from '../component/ViewToggle'
import { NotificationV2 } from '../container/common/NotificationV2'
import { ProfileV2 } from '../container/common/ProfileV2'
import { useAdminTheme } from '../hook/useAdminTheme'
import { RestrictionsV2 } from '../component/system-v2/RestrictionsV2'
import { BlacklistsV2 } from '../component/system-v2/BlacklistsV2'
import { AssistantV2 } from '../component/system-v2/AssistantV2'
import { PdfPromptsV2 } from '../component/system-v2/PdfPromptsV2'
import { CrmSettingsV2 } from '../component/system-v2/CrmSettingsV2'
import { SearchTimeoutsV2 } from '../component/system-v2/SearchTimeoutsV2'
import { ApiLimitsV2 } from '../component/system-v2/ApiLimitsV2'
import { TelegramSettingsV2 } from '../component/system-v2/TelegramSettingsV2'
import { SentimentSettingsV2 } from '../component/system-v2/SentimentSettingsV2'
import { EmailSettingsV2 } from '../component/system-v2/EmailSettingsV2'
import { AbstractApiSettingsV2 } from '../component/system-v2/AbstractApiSettingsV2'
import { SegmentSettingsV2 } from '../component/system-v2/SegmentSettingsV2'
import { SourceIconsV2 } from '../component/system-v2/SourceIconsV2'
import { TabIconsV2 } from '../component/system-v2/TabIconsV2'
import l from './SubscribersPageV2.module.css'
import s from '../component/system-v2/SystemV2.module.css'

/*
 * System settings in the v2 admin design. The classic page (SystemPage.tsx,
 * five tabs) is untouched. Here the sections sit in a list on the left and
 * the open one is in the address (/system_v2#blacklists), so it can be
 * linked to and survives a reload.
 *
 * Every section is redone in the v2 look (system-v2/) on the classic
 * panels' own calls: Restrictions, Blacklists (with a list import), the
 * support and IRBI assistants, and the PDF report prompts. Integrations →
 * CRM sets the CRM type and Zoho keys, Search → Search timeouts the
 * MAX_SEARCH_DURATION_* minutes and Search → API rate limit the
 * QUERY_PER_INTERVAL / TIMEOUT_INTERVAL pair and Integrations → Telegram the
 * campaign bot's TELEGRAM_BOT_WEBHOOK, Integrations → Sentiment analysis
 * the SENTIMENT_ANALYSYS_API_* pair, Integrations → Abstract API the sign-up
 * email/phone checks (ABSTRACT_API_EMAIL_*, ABSTRACT_API_PHONE_*),
 * Integrations → Segment the page-view key (SEGMENT_HTTP_API_KEY), Support → Support email the support inbox
 * (ESPY_EMAIL_SUPPORT), in the database instead of the ConfigMap
 * (/system-settings). Interface → Source icons edits the icon of every search
 * source (/source-icons), Interface → Tab icons the dashboard's and the
 * admin's favicon (/branding).
 */

type SectionKey = 'restrictions' | 'blacklists' | 'support-ai' | 'irbi' | 'pdf' | 'crm' | 'search-timeouts' | 'api-limits' | 'telegram' | 'sentiment' | 'email' | 'abstract-api' | 'segment' | 'source-icons' | 'tab-icons'

const GROUPS: { title: string; items: { key: SectionKey; label: string; sub: string; icon: React.ReactNode }[] }[] = [
  {
    title: 'Access',
    items: [
      { key: 'restrictions', label: 'Restrictions', sub: 'Search sources off for everyone', icon: <ShieldOutlined sx={{ fontSize: 18 }} /> },
      { key: 'blacklists', label: 'Blacklists', sub: 'Emails, phones, IPs', icon: <BlockRounded sx={{ fontSize: 18 }} /> },
    ],
  },
  {
    title: 'AI',
    items: [
      { key: 'support-ai', label: 'Support assistant', sub: 'Tools, model, prompts, knowledge', icon: <SupportAgentOutlined sx={{ fontSize: 18 }} /> },
      { key: 'irbi', label: 'IRBI assistant', sub: 'Model, prompts, knowledge', icon: <SmartToyOutlined sx={{ fontSize: 18 }} /> },
    ],
  },
  {
    title: 'Reports',
    items: [
      { key: 'pdf', label: 'PDF report prompts', sub: 'By country', icon: <PictureAsPdfOutlined sx={{ fontSize: 18 }} /> },
    ],
  },
  {
    title: 'Search',
    items: [
      { key: 'search-timeouts', label: 'Search timeouts', sub: 'Minutes before a search fails', icon: <TimerOutlined sx={{ fontSize: 18 }} /> },
      { key: 'api-limits', label: 'API rate limit', sub: 'Requests per interval', icon: <SpeedOutlined sx={{ fontSize: 18 }} /> },
    ],
  },
  {
    title: 'Interface',
    items: [
      { key: 'source-icons', label: 'Source icons', sub: 'The icon of every source', icon: <InterestsOutlined sx={{ fontSize: 18 }} /> },
      { key: 'tab-icons', label: 'Tab icons', sub: 'Dashboard and admin favicon', icon: <TabOutlined sx={{ fontSize: 18 }} /> },
    ],
  },
  {
    title: 'Integrations',
    items: [
      { key: 'crm', label: 'CRM', sub: 'CRM type, Zoho keys', icon: <HubOutlined sx={{ fontSize: 18 }} /> },
      { key: 'telegram', label: 'Telegram', sub: 'Campaign bot address', icon: <TelegramIcon sx={{ fontSize: 18 }} /> },
      { key: 'sentiment', label: 'Sentiment analysis', sub: 'Service address and key', icon: <PsychologyOutlined sx={{ fontSize: 18 }} /> },
      { key: 'abstract-api', label: 'Abstract API', sub: 'Sign-up email and phone checks', icon: <VerifiedUserOutlined sx={{ fontSize: 18 }} /> },
      { key: 'segment', label: 'Segment', sub: 'Page view analytics', icon: <InsightsOutlined sx={{ fontSize: 18 }} /> },
    ],
  },
  {
    title: 'Support',
    items: [
      { key: 'email', label: 'Support email', sub: 'Where ticket emails go', icon: <MailOutlineRounded sx={{ fontSize: 18 }} /> },
    ],
  },
]
const KEYS = GROUPS.flatMap((g) => g.items.map((i) => i.key))
const fromHash = (): SectionKey => {
  const h = window.location.hash.replace('#', '') as SectionKey
  return KEYS.includes(h) ? h : 'restrictions'
}

export function SystemPageV2() {
  const { theme, toggleTheme } = useAdminTheme()
  const [section, setSection] = React.useState<SectionKey>(fromHash)

  React.useEffect(() => {
    const onHash = () => setSection(fromHash())
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])
  const open = (key: SectionKey) => {
    setSection(key)
    window.history.replaceState(null, '', `${window.location.pathname}#${key}`)
  }

  const header = (
    <div className={`${l.theme} ${l.header}`} data-theme={theme}>
      <div className={l.crumb}><small>Settings</small><b>System</b></div>
      <div className={l.spacer} />
      <ViewToggle dark={theme === 'dark'} />
      <Tooltip title={theme === 'dark' ? 'Light theme' : 'Dark theme'}>
        <button className={l.iconBtn} onClick={toggleTheme} aria-label="Toggle theme">
          {theme === 'dark' ? <LightModeIcon sx={{ fontSize: 17 }} /> : <DarkModeIcon sx={{ fontSize: 17 }} />}
        </button>
      </Tooltip>
      <NotificationV2 />
      <ProfileV2 />
    </div>
  )

  return (
    <PrivatePage header={header}>
      <div className={`${l.theme} ${l.page}`} data-theme={theme}>
        <div className={s.layout}>
          <nav className={s.nav} aria-label="System sections">
            {GROUPS.map((g) => (
              <div key={g.title} className={s.navGroup}>
                <small>{g.title}</small>
                {g.items.map((i) => (
                  <button key={i.key} type="button" className={`${s.navItem} ${section === i.key ? s.navOn : ''}`} onClick={() => open(i.key)}>
                    <span className={s.navIcon}>{i.icon}</span>
                    <span className={s.navText}><b>{i.label}</b><span>{i.sub}</span></span>
                  </button>
                ))}
              </div>
            ))}
          </nav>

          <main className={s.content}>
            {section === 'restrictions' && <RestrictionsV2 />}
            {section === 'blacklists' && <BlacklistsV2 />}
            {section === 'support-ai' && <AssistantV2 key="support" which="support" />}
            {section === 'irbi' && <AssistantV2 key="irbi" which="irbi" />}
            {section === 'pdf' && <PdfPromptsV2 />}
            {section === 'crm' && <CrmSettingsV2 />}
            {section === 'search-timeouts' && <SearchTimeoutsV2 />}
            {section === 'api-limits' && <ApiLimitsV2 />}
            {section === 'telegram' && <TelegramSettingsV2 />}
            {section === 'sentiment' && <SentimentSettingsV2 />}
            {section === 'email' && <EmailSettingsV2 />}
            {section === 'abstract-api' && <AbstractApiSettingsV2 />}
            {section === 'segment' && <SegmentSettingsV2 />}
            {section === 'source-icons' && <SourceIconsV2 />}
            {section === 'tab-icons' && <TabIconsV2 />}
          </main>
        </div>
      </div>
    </PrivatePage>
  )
}
